import { useCallback, useEffect, useState } from "react";
import { useEstablishments } from "@/context/EstablishmentContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";
import { ActivityStart } from "@/components/activity/ActivityStart";
import { ActivityConfirm } from "@/components/activity/ActivityConfirm";
import { ActivityResult } from "@/components/activity/ActivityResult";
import { ActivityHistory } from "@/components/activity/ActivityHistory";
import { ActivityCompare } from "@/components/activity/ActivityCompare";
import { ActivityProducts } from "@/components/activity/ActivityProducts";
import { StatsOverview, StatsClients, StatsTraffic, Indicators, ManualEntry, DataSources } from "@/components/activity/StatsCenter";
import { Button } from "@/components/ui/button";

const TABS = [["overview", "Vue d'ensemble"], ["clients", "Clients"], ["products", "Produits"], ["traffic", "Fréquentation"], ["compare", "Comparaison"], ["indicators", "Mes statistiques"], ["data", "Imports & données"]];

export default function ActivityInsights() {
  const { current } = useEstablishments();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [draft, setDraft] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab"); return t === "stats" ? "indicators" : (t && TABS.some(([k]) => k === t) ? t : "overview"); });
  const [preset, setPreset] = useState(null);
  const [manual, setManual] = useState(false);
  const [autoInd, setAutoInd] = useState(false);

  const load = useCallback(async () => {
    if (!current) return;
    try { const [a, s] = await Promise.all([api.get(`/establishments/${current.id}/activity`), api.get(`/establishments/${current.id}/stats`)]); setData(a.data); setStats(s.data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [current]);
  useEffect(() => { setDraft(null); setAnalysis(null); load(); }, [load]);

  const analyze = async (period) => {
    setBusy(true);
    try { const { data: a } = await api.post(`/activity/periods/${period.id}/analyze`, {}); setAnalysis(a); setDraft(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const goImport = () => { setTab("data"); setManual(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goManual = () => { setTab("data"); setManual(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const goIndicator = () => { setTab("indicators"); setAutoInd(true); };
  const visibleTabs = TABS.filter(([k]) => !(k === "products" && stats && !stats.available.products && !stats.available.activity) && !(k === "compare" && stats && !stats.available.activity));

  if (!current) return <div className="p-6 text-slate-500">Sélectionnez un établissement.</div>;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="activity-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="w-7 h-7 text-blue-600" /> Statistiques & Activité</h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Saisissez, importez (photo, PDF, CSV, Excel) ou créez vos indicateurs : chaque donnée est vérifiée par vous, puis Lia la croise avec vos achats, votre équipe, votre fidélité et vos jeux.</p>
      </div>
      {analysis ? (
        <ActivityResult analysis={analysis} onBack={() => setAnalysis(null)} onRefresh={(a) => setAnalysis(a)} />
      ) : draft ? (
        <ActivityConfirm period={draft} profile={data?.profile} onChange={setDraft} onCancel={() => setDraft(null)} onConfirmed={analyze} busy={busy} />
      ) : (
        <>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit max-w-full overflow-x-auto" data-testid="activity-tabs">
            {visibleTabs.map(([k, l]) => <Button key={k} size="sm" variant={tab === k ? "default" : "ghost"} onClick={() => setTab(k)} className="rounded-lg shrink-0" data-testid={`activity-tab-${k}`}>{l}</Button>)}
          </div>
          {tab === "overview" && stats && <StatsOverview d={stats} onManual={goManual} onImport={goImport} onIndicator={goIndicator} />}
          {tab === "clients" && stats && <StatsClients d={stats} />}
          {tab === "products" && <ActivityProducts est={current} onImport={goImport} />}
          {tab === "traffic" && stats && <StatsTraffic d={stats} />}
          {tab === "compare" && <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-3">
            <h2 className="font-heading font-bold text-base md:text-lg">Comparer deux périodes</h2>
            <ActivityCompare est={current} profile={data?.profile} saved={(data?.periods || []).filter((p) => p.status === "confirmed")} preset={preset} onResult={(a) => { setAnalysis(a); setPreset(null); load(); }} onImport={goImport} />
          </section>}
          {tab === "indicators" && <Indicators est={current} onChanged={load} autoOpen={autoInd} />}
          {tab === "data" && <div className="space-y-4">
            {manual ? <ManualEntry est={current} onDone={(p) => { setManual(false); setDraft(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} onCancel={() => setManual(false)} /> : <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setManual(true)} data-testid="data-manual-btn">Saisir des statistiques manuellement</Button>}
            <ActivityStart est={current} profile={data?.profile} quota={data?.quota} isDemo={data?.is_demo} onDraft={setDraft} />
            <ActivityHistory data={data} onOpen={(a) => setAnalysis(a)} onAnalyze={analyze} onReload={load} onCompare={(p) => { setPreset(p); setTab("compare"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
            <section className="space-y-2"><h2 className="font-heading font-bold text-base">Journal des données</h2><p className="text-xs text-slate-500">Origine, période, validation et modifications de chaque donnée.</p><DataSources sources={stats?.sources} /></section>
          </div>}
        </>
      )}
    </div>
  );
}
